/**
 * The second consent this tracker answers to: the fan's loyalty opt-in.
 *
 * **Why this exists.** `consent.js` gates collection on a OneTrust analytics group, which is a
 * decision about analytics in general. It says nothing about `app_metadata.loyaltyoptin`, so a fan
 * who accepted the cookie banner and never joined the loyalty programme was still having pageviews —
 * and, once signed in, their loyalty JWT as `context.jwt` — sent to 3Halves, who are the loyalty
 * vendor. Two consents, one data flow, one processor. This is the second one.
 *
 * **It reads a DOM attribute rather than importing the consent module, and that is not laziness.**
 * [D-055](../../../../docs/decisions.md) forbids the `app` entry importing from
 * `resources/scripts/loyalty/`: the two entries share one Rollup build, so an import would hoist a
 * shared chunk and rewrite the module graph of the already-shipped loyalty bundle — the bundle that
 * carries the fan's access token. `<html data-loyalty-consent>` is published by
 * `resources/scripts/loyalty/consent/index.js` for exactly the diagnostic reason `data-loyalty-toast`
 * and `data-score-tracker` are, and consuming it is a one-way string contract instead of a module
 * edge. **The published vocabulary is therefore frozen** — see `GRANTED` below.
 *
 * **It fails closed, and that is the whole point.** Anything other than `granted` — including the
 * attribute being absent because the loyalty bundle has not answered yet, or is not on the page, or
 * is switched off — means no analytics. An anonymous visitor can never hold loyalty consent, so
 * enabling this stops collection for them entirely. That is the requirement, not a side effect.
 *
 * Nothing is imported here, so `loyaltyConsentGate.test.js` drives the whole thing under
 * `node --test` with no DOM. `index.js` wires the real one.
 */

/**
 * The one attribute value that grants.
 *
 * Frozen by the cross-bundle contract above. `resources/scripts/loyalty/consent/machine.js`'s
 * `STATE` also emits `off`, `anon`, `reading`, `denied` and `unavailable`; every one of them means
 * no, and a value this file has never heard of must mean no as well.
 */
export const GRANTED = 'granted';

/** The attribute the loyalty bundle publishes to. */
export const CONSENT_ATTRIBUTE = 'data-loyalty-consent';

/**
 * The SDK's consent categories for a given loyalty answer.
 *
 * @param {boolean} granted Whether loyalty consent is held.
 * @return {{analytics: boolean, personalization: boolean, advertising: boolean}} The categories.
 */
export function analyticsCategories( granted ) {
	return {
		analytics: Boolean( granted ),
		// Hardcoded false, exactly as consent.js keeps them, and for the reason it gives: the SDK
		// drops any event declaring a false category, so sponsor and ad events cannot fire by
		// accident. Nothing about which consent gates analytics changes that.
		personalization: false,
		advertising: false,
	};
}

/**
 * Build the gate over injected readers.
 *
 * @param {Object}                                 options         Collaborators.
 * @param {() => string}                           options.read    Current attribute value, '' when absent.
 * @param {(listener: () => void) => (() => void)} options.observe Change feed; returns an unsubscribe.
 * @return {{read: () => boolean, subscribe: Function}} The gate.
 */
export function createLoyaltyConsentGate( { read, observe } ) {
	/**
	 * @return {boolean} Whether the fan has granted loyalty consent.
	 */
	function granted() {
		return read() === GRANTED;
	}

	/**
	 * Notify on a real change only.
	 *
	 * The attribute moves through several values on its way to an answer (`reading` before
	 * `granted`, for instance), and every change to it would otherwise restart the tracker — which
	 * re-emits the automatic pageview. That is the same inflation `consent.js`'s change guard exists
	 * to prevent, so the filter is on the BOOLEAN rather than on the attribute.
	 *
	 * Seeded at subscription time, so the first notification is a change from what the caller has
	 * already acted on.
	 *
	 * @param {(granted: boolean) => void} listener Called on each transition.
	 * @return {() => void} Unsubscribe.
	 */
	function subscribe( listener ) {
		let last = granted();

		return observe( () => {
			const next = granted();

			if ( next === last ) {
				return;
			}

			last = next;
			listener( next );
		} );
	}

	return { read: granted, subscribe };
}

/**
 * Read the attribute off the live document.
 *
 * @return {string} The value, or '' when absent or outside a browser.
 */
export function readConsentAttribute() {
	if ( typeof document === 'undefined' || ! document.documentElement ) {
		return '';
	}

	return document.documentElement.getAttribute( CONSENT_ATTRIBUTE ) || '';
}

/**
 * Watch the attribute on the live document.
 *
 * A `MutationObserver` rather than a poll: the loyalty bundle sets this attribute a handful of times
 * per page at most, and an observer costs nothing while nothing changes.
 *
 * @param {() => void} listener Called on each attribute write.
 * @return {() => void} Unsubscribe.
 */
export function observeConsentAttribute( listener ) {
	if ( typeof MutationObserver === 'undefined' || typeof document === 'undefined' ) {
		return () => {};
	}

	const observer = new MutationObserver( listener );

	observer.observe( document.documentElement, {
		attributes: true,
		attributeFilter: [ CONSENT_ATTRIBUTE ],
	} );

	return () => observer.disconnect();
}
